/**
 * WorkspaceSwitcher — rendered only when the user belongs to 2+ workspaces.
 * Switching asks the API (which verifies membership) to set the primary
 * workspace, then reloads so every cached query refetches against it.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listWorkspaces, switchWorkspace } from '../../lib/team';

export function WorkspaceSwitcher() {
  const [error, setError] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ['workspaces'], queryFn: listWorkspaces, staleTime: 60_000 });
  if (!data || data.workspaces.length < 2) return null;

  async function onChange(accountId: string) {
    if (accountId === data!.active_account_id) return;
    setError(null);
    try {
      await switchWorkspace(accountId);
      window.location.assign('/dashboard');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not switch workspace');
    }
  }

  return (
    <div className="px-3 mb-2">
      <select
        aria-label="Switch workspace"
        value={data.active_account_id}
        onChange={(e) => void onChange(e.target.value)}
        className="w-full rounded-lg border border-border bg-surface-1 px-2 py-2 text-2xs text-ink focus:border-primary/50 focus:outline-hidden min-h-[44px] md:min-h-0"
      >
        {data.workspaces.map((w) => (
          <option key={w.account_id} value={w.account_id}>{w.name}</option>
        ))}
      </select>
      {error && <p className="mt-1 text-2xs text-danger">{error}</p>}
    </div>
  );
}
